/**
 * (c) 2021, vincibot Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import {
  Box,
  Divider,
  List,
  ListItem,
  HStack,
  VStack,
  Text,
  Stack,
} from "@chakra-ui/layout";
import { Button, IconButton } from "@chakra-ui/button";
import { Link, useMediaQuery, Spinner } from "@chakra-ui/react";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import AreaHeading from "../../common/AreaHeading";
import HeadedScrollablePanel from "../../common/HeadedScrollablePanel";
import { Anchor, useRouterTabSlug, useRouterState } from "../../router-hooks";
import { useAnimationDirection } from "../common/documentation-animation-hooks";
import { RiArrowDownLine } from "react-icons/ri";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { heightMd, widthXl } from "../../common/media-queries";
import { ExtensionModule } from "../mapping/model";
import { py } from './plugins-py'
import { useProjectActions } from "../../project/project-hooks";
// import { useBleState } from '../../vincibot/ble-state-hook'
import { useToast } from "@chakra-ui/react";

import { useSelector, useDispatch } from 'react-redux';

// 本地存储的键名
const EXTENSIONS_STORAGE_KEY = 'extensions_status';

// 本地存储工具函数
const saveExtensionsToLocal = (extensionStatus: Record<string, boolean>, loadingStatus: Record<string, boolean>, hasChecked: boolean) => {
  const data = {
    extensionStatus,
    loadingStatus,
    hasChecked
  };
  localStorage.setItem(EXTENSIONS_STORAGE_KEY, JSON.stringify(data));
};

const loadExtensionsFromLocal = () => {
  try {
    const stored = localStorage.getItem(EXTENSIONS_STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      return {
        extensionStatus: data.extensionStatus || {},
        loadingStatus: data.loadingStatus || {},
        hasChecked: data.hasChecked || false
      };
    }
  } catch (error) {
    console.error('加载本地扩展状态失败:', error);
  }
  return {
    extensionStatus: {},
    loadingStatus: {},
    hasChecked: false
  };
};

const clearExtensionsFromLocal = () => {
  localStorage.removeItem(EXTENSIONS_STORAGE_KEY);
};

interface ApiDocumentationProps {
  extensions: ExtensionModule[];
}
declare const window: any;

export const ExtensionsDocumentation = ({
  extensions,
}: ApiDocumentationProps) => {
  const [anchor, setAnchor] = useRouterTabSlug("api");
  const handleNavigate = useCallback(
    (id: string | undefined) => {
      setAnchor(id ? { id } : undefined, "documentation-user");
    },
    [setAnchor]
  );
  const direction = useAnimationDirection(anchor);
  return (
    <ActiveLevel
      key={anchor ? 0 : 1}
      anchor={anchor}
      onNavigate={handleNavigate}
      extensions={extensions}
      direction={direction}
    />
  );
};

interface ActiveLevelProps {
  anchor: Anchor | undefined;
  extensions: ExtensionModule[];
  onNavigate: (state: string | undefined) => void;
  direction: "forward" | "back" | "none";
}

const ActiveLevel = ({
  anchor,
  onNavigate,
  extensions,
  direction,
}: ActiveLevelProps) => {
  const intl = useIntl();
  const [isShortWindow] = useMediaQuery(heightMd);
  const [isWideScreen] = useMediaQuery(widthXl);
  const extensionString = intl.formatMessage({ id: "plugins-tab" });
  const toast = useToast();

  // 从本地存储初始化状态
  const localData = loadExtensionsFromLocal();
  const [extensionStatus, setExtensionStatus] = useState<Record<string, boolean>>(localData.extensionStatus);
  const [loadingStatus, setLoadingStatus] = useState<Record<string, boolean>>(localData.loadingStatus);
  const hasCheckedExtensions = useRef(localData.hasChecked);

  // const bleState = useBleState()
  const actions = useProjectActions();
  const [, setParams] = useRouterState();
  const downloadRef = useRef<HTMLButtonElement>(null);

  // 保存状态到本地存储的函数
  const saveToLocal = useCallback((extStatus: Record<string, boolean>, loadStatus: Record<string, boolean>, checked: boolean) => {
    saveExtensionsToLocal(extStatus, loadStatus, checked);
  }, []);

  // 监听状态变化并保存到本地存储
  useEffect(() => {
    saveToLocal(extensionStatus, loadingStatus, hasCheckedExtensions.current);
  }, [extensionStatus, loadingStatus, saveToLocal]);

  const handleReferenceLink = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setParams({ tab: "reference" });
    },
    [setParams]
  );

  const getNewVersion = (name: string) => {
    const code = py[name];
    const versionRegex = /version\s*=\s*([\d.]+)/;
    const match = code.match(versionRegex);
    return match ? match[1] : null;
  }

  const flashing = useRef<{ flashing: boolean; lastCompleteFlash: number }>({
    flashing: false,
    lastCompleteFlash: 0,
  });

  //烧录扩展模块
  const handleUploadUpload = async (id: string) => {
    // 检查是否有任何扩展正在loading状态
    const hasAnyLoading = Object.values(loadingStatus).some(status => status === true);
    if (hasAnyLoading) {
      // 提示正在下载
      toast({
        title: "提示",
        description: "有扩展正在下载中，请等待完成后再试",
        status: "info",
        duration: 3000,
        isClosable: true,
        position: "top"
      });
      return;
    }

    //step1:判断是否连接
    if (!window.peripheral.isConnected()) {
      //弹窗去连接
      toast({
        title: "提示",
        description: "请先连接设备",
        status: "info",
        duration: 3000,
        isClosable: true,
        position: "top"
      });
      return;
    } else {
      //step2:获取扩展代码
      // 设置当前扩展为loading状态
      const newLoadingStatus = {
        ...loadingStatus,
        [id]: true,
      };
      setLoadingStatus(newLoadingStatus);

      try {
        //step2:获取扩展代码
        const pythonCode = py[id];
        // 等待上传完成
        const res = await window.peripheral.uploadPy(pythonCode, `${id}.py`);
        console.log('上传结果:', res);
        if (res) {
          const newExtensionStatus = {
            ...extensionStatus,
            [id]: true,
          };
          setExtensionStatus(newExtensionStatus);
        } else {
          console.warn('上传失败，不更新扩展状态');
        }

      } catch (e) {
        console.warn(e, '上传失败');
      } finally {
        // 无论成功失败，都要清除loading状态
        const finalLoadingStatus = {
          ...loadingStatus,
          [id]: false,
        };
        setLoadingStatus(finalLoadingStatus);
      }
    }
  }

  const checkExtensions = async () => {
    if (hasCheckedExtensions.current) {
      console.log('扩展已检查过，跳过重复检查');
      return;
    }

    // 使用队列方式依次检查，避免并发问题
    for (const item of extensions) {
      // 开始检查当前扩展时，设置其loading状态
      setLoadingStatus((prev) => ({
        ...prev,
        [item.id]: true,
      }));

      try {
        // 当前版本
        const newVersion = getNewVersion(item.id);
        const version = await window.peripheral.checkPyVersion(item.id);
        console.warn('检查扩展版本', item.id, 'newVersion:', newVersion, 'deviceVersion:', version);

        const isValid = version >= newVersion;

        // 立即更新当前扩展的状态
        setExtensionStatus((prev) => ({
          ...prev,
          [item.id]: isValid,
        }));
      } catch (error) {
        console.error(`检查扩展 ${item.id} 时出错:`, error);

        // 出错时也立即更新状态
        setExtensionStatus((prev) => ({
          ...prev,
          [item.id]: false,
        }));
      } finally {
        // 检查完成后，立即清除当前扩展的loading状态
        setLoadingStatus((prev) => ({
          ...prev,
          [item.id]: false,
        }));
      }
    }

    console.log('所有扩展检查完成');
    hasCheckedExtensions.current = true;
    // 立即保存到本地存储
    saveToLocal(extensionStatus, loadingStatus, true);
  }
  // 获取连接状态
  const isCheckExtension = useSelector((state: any) => state.connectStore.checkExtension);
  useEffect(() => {
    if (!isCheckExtension) {
      setExtensionStatus({});
      setLoadingStatus({});
      hasCheckedExtensions.current = false;
      clearExtensionsFromLocal();
      return;
    } else {
      checkExtensions()
    }
  }, [isCheckExtension])

  return (
    <HeadedScrollablePanel
      direction={direction}
      heading={
        <AreaHeading
          name={extensionString}
          description={
            <FormattedMessage
              id="plugins-description"
              values={{
                link: (chunks: ReactNode) => (
                  <Link color="brand.500" onClick={handleReferenceLink} href="">
                    {chunks}
                  </Link>
                ),
              }as any}
            />
          }
        />
      }
    >
      <List flex="1 1 auto" m={3}>
        {extensions.map((module, index) => {
          const isExtensionExists = extensionStatus[module.id] || false;
          const isLoading = loadingStatus[module.id] || false;
          return (
            <ListItem key={index}>
              <HStack
                my={3}
                mr={3}
                ml={5}
                spacing={isShortWindow || !isWideScreen ? 3 : 5}
              >
                <VStack
                  alignItems="stretch"
                  spacing={isShortWindow || !isWideScreen ? 0 : 1}
                  flex="1 1 auto"
                >
                  <HStack justifyContent="space-between">
                    {/* 标题和描述 */}
                    <Stack alignItems="start">
                      <Text as="h3" fontSize="lg" fontWeight="semibold">
                        {module.name}
                      </Text>
                      <Box fontSize="sm" noOfLines={1}>
                        {module.desc}
                      </Box>
                    </Stack>
                    {/* 根据状态显示不同图标 */}
                    {isLoading ? (
                      <Spinner size="md" color="brand.500" />
                    ) : isExtensionExists ? (
                      <AiOutlineCheckCircle fontSize="1.35rem" color="#6cd27c" />
                    ) : (
                      <IconButton
                        icon={<RiArrowDownLine />}
                        aria-label={module.name}
                        size="sm"
                        color="brand.800"
                        variant="ghost"
                        fontSize="2xl"
                        ref={downloadRef}
                        onClick={() => handleUploadUpload(module.id)}
                      />
                    )}
                  </HStack>
                </VStack>
              </HStack>
              <Divider ml={3} borderWidth="1px" />
            </ListItem>
          )
        })}
      </List>
    </HeadedScrollablePanel>
  );
};
